import type { ApolloClient } from 'apollo-client'
import {
  MESSAGE_APPEARANCE_ACKS_BY_MESSAGE_APPEARANCE_IDS_V2_QUERY
} from '../../../message-appearance-v2/hooks/useMessageAppearanceAcksByMessageAppearanceIdsV2'
import type {
  MessageAppearanceAcksByMessageAppearanceIdsV2,
  MessageAppearanceAcksByMessageAppearanceIdsV2Variables
} from '../../../message-appearance-v2/hooks/__generated__/MessageAppearanceAcksByMessageAppearanceIdsV2'
import { getAckTimestampForDelay } from '../../../message-appearance-v2/ackTimestamp'

const ACK_BATCH_SIZE = 25

const chunkIds = (ids: string[], size: number) => {
  const chunks: string[][] = []
  for (let i = 0; i < ids.length; i += size) {
    chunks.push(ids.slice(i, i + size))
  }
  return chunks
}

export class ParentAckLoader {
  private readonly client: ApolloClient<object>
  private readonly cacheByParentId = new Map<string, Map<string, number | null>>()

  constructor(client: ApolloClient<object>) {
    this.client = client
  }

  async ensureParentAcks(parentId: string, appearanceIds: string[]) {
    const cache = this.cacheByParentId.get(parentId) || new Map<string, number | null>()
    const idsToFetch = appearanceIds.filter(id => !cache.has(id))
    if (idsToFetch.length === 0) {
      return cache
    }

    for (const chunk of chunkIds(idsToFetch, ACK_BATCH_SIZE)) {
      const result = await this.client.query<
        MessageAppearanceAcksByMessageAppearanceIdsV2,
        MessageAppearanceAcksByMessageAppearanceIdsV2Variables
      >({
        query: MESSAGE_APPEARANCE_ACKS_BY_MESSAGE_APPEARANCE_IDS_V2_QUERY,
        variables: { messageAppearanceIds: chunk },
        fetchPolicy: 'network-only'
      })

      const rows = result.data?.messageAppearanceAcksByMessageAppearanceIdsV2 || []
      const ackById = new Map<string, number>()
      for (const row of rows) {
        if (row?.messageAppearanceId != null && row.clientTimestamp != null) {
          ackById.set(row.messageAppearanceId, getAckTimestampForDelay(row))
        }
      }

      for (const id of chunk) {
        cache.set(id, ackById.get(id) ?? null)
      }
    }

    this.cacheByParentId.set(parentId, cache)
    return cache
  }

  reset() {
    this.cacheByParentId.clear()
  }
}
